import ISearchParams from '@shared/models/ISearchParams';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useScrollToElement from './useScrollToElement';

export default function useManagedOrgsSearch() {
    const { t } = useTranslation();
    const { organizationId } = useParams<{ organizationId: string }>();
    const { showError } = AlertsContainer.useContainer();
    const organizationContainer = OrganizationContainer.useContainer();
    const managedOrgsSearchContentId = 'managed-orgs-search';
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });

    useScrollToElement(managedOrgsSearchContentId, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(
        () => organizationContainer.orgManagedOrgs.length >= displayLimit,
        [organizationContainer.orgManagedOrgs]
    );

    const DEBOUNCE_DELAY = 500;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery.toLowerCase(),
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleNext = () => {
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            if (organizationContainer.loadingDataRef.current.managedOrgs) return;
            if (!organizationId) return;
            try {
                organizationContainer.handleLoadingDataChange('managedOrgs', true);
                await organizationContainer.getOrgManagedOrgsAll(organizationId, debouncedQuery);
            } catch (e) {
                showError(t('common:organization.managed.error'));
            } finally {
                organizationContainer.handleLoadingDataChange('managedOrgs', false);
            }
        };
        trySearch();
    }, [debouncedQuery]);

    return {
        results: organizationContainer.orgManagedOrgs,
        managedOrgsSearchContentId,
        handleSearchQueryChange,
        searchQuery,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        isLoading: organizationContainer.loadingData.managedOrgs,
    };
}
